/**
 * Event snapshot numbers for sponsor follow-up emails — matches Memory Book “By the numbers”
 * logic (including statsOverrides from eventMemoryMeta) where possible.
 */
import { getAgeFromYear } from './flyerDisplay';

const CANOPY_THUR = 'Canopy Thursday';
const CANOPY_FRI = 'Canopy Friday';
const WATER_THUR = 'Water Training (Thur)';
const WATER_FRI = 'Water Training (Fri)';
const TICKET_HIGH_ALTITUDE = 'High Altitude';
const TICKET_JET = 'Jet';

const MEMORY_BOOK_TEASER_SLIDE_ORDER = [
  'freshlings',
  'teams',
  'mentors',
  'avgJumps',
  'ages',
  'ha',
  'jet',
  'canopy',
  'water',
  'states',
  'countries',
];

const MEMORY_BOOK_TEASER_LABELS = {
  freshlings: 'Freshlings',
  teams: 'Teams',
  mentors: 'Mentors',
  avgJumps: 'Avg. jumps at start',
  ages: 'Freshling ages',
  ha: 'High altitude jumps',
  jet: 'Jet jumps',
  canopy: 'Canopy courses',
  water: 'Water training',
  states: 'States',
  countries: 'Countries',
};

const MEMORY_BOOK_CHIP_TONE_BY_ID = {
  teams: 'slate',
  mentors: 'amber',
  avgJumps: 'violet',
  states: 'teal',
  countries: 'indigo',
};

function memoryBookSlideOrderIndex(id) {
  const i = MEMORY_BOOK_TEASER_SLIDE_ORDER.indexOf(id);
  return i === -1 ? 999 : i;
}

function usageOrManifestCount(flyer, label) {
  const u = flyer?.usage || {};
  const mt = flyer?.manifestTickets || {};
  const used = u[label]?.used;
  const manifest = mt[label];
  if (Number.isFinite(used) && used > 0) return used;
  if (Number.isFinite(manifest) && manifest > 0) return manifest;
  if (Number.isFinite(used) && used > 0) return used;
  return 0;
}

function sumUsageOrManifest(flyers, label) {
  return flyers.reduce((sum, f) => sum + usageOrManifestCount(f, label), 0);
}

function isWithdrawnOrDuplicate(flyer) {
  if (!flyer) return false;
  const statusText = (flyer.status || '').toString().toLowerCase();
  const notesText = (flyer.notes || '').toString().toLowerCase();
  return (
    statusText.includes('withdrawn') ||
    statusText.includes('duplicate') ||
    statusText.includes('cancelled') ||
    statusText.includes('canceled') ||
    notesText.includes('withdrawn') ||
    notesText.includes('duplicate') ||
    notesText.includes('cancelled') ||
    notesText.includes('canceled')
  );
}

function isActiveForActivityStats(flyer) {
  if (!flyer) return false;
  if (flyer.test) return false;
  if (flyer.cancelled) return false;
  if (isWithdrawnOrDuplicate(flyer)) return false;
  if (flyer.type === 'mentor') {
    const statusText = (flyer.status || '').toString().toLowerCase();
    if (!statusText.includes('accept')) return false;
  }
  return true;
}

function applyMemoryBookStatsOverrides(data, rawOverrides) {
  if (!data || !rawOverrides || typeof rawOverrides !== 'object' || Object.keys(rawOverrides).length === 0) {
    return data;
  }
  const o = rawOverrides;

  const patchTeaserSlide = (slide) => {
    const patch = o[slide.id];
    if (!patch || typeof patch !== 'object') return slide;
    const next = { ...slide };
    if (slide.kind === 'text') {
      if (patch.display != null && String(patch.display).trim()) next.display = String(patch.display).trim();
      if (patch.label != null && String(patch.label).trim()) next.label = String(patch.label).trim();
    } else {
      if (patch.value != null && Number.isFinite(Number(patch.value))) next.value = Number(patch.value);
      if (patch.label != null && String(patch.label).trim()) next.label = String(patch.label).trim();
    }
    return next;
  };

  let teaserSlides = (data.teaserSlides || []).map(patchTeaserSlide);
  const seenIds = new Set(teaserSlides.map((s) => s.id));

  for (const id of MEMORY_BOOK_TEASER_SLIDE_ORDER) {
    if (seenIds.has(id)) continue;
    const patch = o[id];
    if (!patch || typeof patch !== 'object') continue;
    if (id === 'ages') {
      const d = patch.display != null && String(patch.display).trim();
      if (d) {
        teaserSlides.push({
          id: 'ages',
          kind: 'text',
          display: d,
          label: (patch.label && String(patch.label).trim()) || MEMORY_BOOK_TEASER_LABELS.ages,
        });
        seenIds.add(id);
      }
    } else {
      const v = patch.value != null ? Number(patch.value) : NaN;
      if (Number.isFinite(v)) {
        teaserSlides.push({
          id,
          kind: 'number',
          value: v,
          label: (patch.label && String(patch.label).trim()) || MEMORY_BOOK_TEASER_LABELS[id] || id,
        });
        seenIds.add(id);
      }
    }
  }

  teaserSlides = [...teaserSlides].sort(
    (a, b) => memoryBookSlideOrderIndex(a.id) - memoryBookSlideOrderIndex(b.id)
  );

  let hero = { ...data.hero };
  if (o.hero && typeof o.hero === 'object') {
    if (o.hero.value != null && Number.isFinite(Number(o.hero.value))) hero.value = Number(o.hero.value);
    if (o.hero.label != null && String(o.hero.label).trim()) hero.label = String(o.hero.label).trim();
  }

  const chipIds = ['teams', 'mentors', 'avgJumps', 'states', 'countries'];
  let chips = (data.chips || []).map((c) => {
    const patch = o[c.id];
    if (!patch || typeof patch !== 'object') return c;
    const next = { ...c };
    if (patch.value != null && Number.isFinite(Number(patch.value))) next.value = Number(patch.value);
    if (patch.label != null && String(patch.label).trim()) next.label = String(patch.label).trim();
    return next;
  });
  const chipSeen = new Set(chips.map((c) => c.id));
  for (const id of chipIds) {
    if (chipSeen.has(id)) continue;
    const patch = o[id];
    if (!patch || typeof patch !== 'object') continue;
    const v = patch.value != null ? Number(patch.value) : NaN;
    if (!Number.isFinite(v)) continue;
    chips.push({
      id,
      label: (patch.label && String(patch.label).trim()) || MEMORY_BOOK_TEASER_LABELS[id] || id,
      value: v,
      tone: MEMORY_BOOK_CHIP_TONE_BY_ID[id] || 'slate',
    });
    chipSeen.add(id);
  }
  chips = [...chips].sort((a, b) => memoryBookSlideOrderIndex(a.id) - memoryBookSlideOrderIndex(b.id));

  let ageRange = data.ageRange ? { ...data.ageRange } : null;
  if (o.ages && typeof o.ages === 'object') {
    const d = o.ages.display != null && String(o.ages.display).trim();
    const lbl = o.ages.label != null && String(o.ages.label).trim();
    if (d || lbl) {
      ageRange = {
        text: d || ageRange?.text || '',
        label: lbl || ageRange?.label || MEMORY_BOOK_TEASER_LABELS.ages,
      };
    }
  }

  let activities = { ...(data.activities || {}) };
  if (o.activities && typeof o.activities === 'object') {
    for (const k of ['highAltitude', 'jet', 'canopy', 'water']) {
      const v = o.activities[k];
      if (v != null && Number.isFinite(Number(v))) activities[k] = Number(v);
    }
  }
  const hasAnyActivity =
    (activities.highAltitude || 0) > 0 ||
    (activities.jet || 0) > 0 ||
    (activities.canopy || 0) > 0 ||
    (activities.water || 0) > 0;

  return {
    ...data,
    teaserSlides,
    hero,
    chips,
    ageRange,
    activities,
    hasAnyActivity,
  };
}

function buildNumbersDataBase({ flyers, nonTestTeams, dzMappings }) {
  const totalTeams = nonTestTeams.length;
  const activeActivityFlyers = flyers.filter(isActiveForActivityStats);
  const pairedEmails = new Set(
    nonTestTeams.flatMap((t) => (t.freshlings || []).map((e) => (e || '').trim().toLowerCase())).filter(Boolean)
  );
  const pairedFreshlings = flyers.filter(
    (f) =>
      f.type === 'freshling' &&
      !f.test &&
      !f.cancelled &&
      !isWithdrawnOrDuplicate(f) &&
      pairedEmails.has((f.email || '').trim().toLowerCase())
  );
  const pairedFreshlingCount = pairedFreshlings.length;
  const jumpNumbers = pairedFreshlings
    .map((f) => parseInt(f.jumpNumber, 10))
    .filter((n) => Number.isFinite(n) && n >= 0);
  const avgJumps =
    jumpNumbers.length > 0 ? Math.round(jumpNumbers.reduce((a, b) => a + b, 0) / jumpNumbers.length) : null;
  const pairedMentors = new Set(
    nonTestTeams.map((t) => (t.mentor || '').toString().trim().toLowerCase()).filter(Boolean)
  ).size;
  const canopyUsed =
    activeActivityFlyers.reduce((sum, f) => {
      return sum + usageOrManifestCount(f, CANOPY_THUR) + usageOrManifestCount(f, CANOPY_FRI);
    }, 0) || 0;
  const waterUsed =
    activeActivityFlyers.reduce((sum, f) => {
      return sum + usageOrManifestCount(f, WATER_THUR) + usageOrManifestCount(f, WATER_FRI);
    }, 0) || 0;

  const ages = pairedFreshlings
    .map((f) => getAgeFromYear(f.yearBorn))
    .filter((a) => a != null && a > 0 && a < 120);
  const youngest = ages.length > 0 ? Math.min(...ages) : null;
  const oldest = ages.length > 0 ? Math.max(...ages) : null;
  const ageRangeLabel =
    youngest != null && oldest != null
      ? youngest === oldest
        ? `${youngest} yrs`
        : `${youngest}–${oldest} yrs`
      : null;

  const highAltFromUsage = sumUsageOrManifest(activeActivityFlyers, TICKET_HIGH_ALTITUDE);
  const highAltitudeJumps = highAltFromUsage;
  const jetJumps = sumUsageOrManifest(activeActivityFlyers, TICKET_JET);

  const geoFreshlings = flyers.filter(
    (f) => f.type === 'freshling' && !f.test && !f.cancelled && !isWithdrawnOrDuplicate(f)
  );
  const statesSet = new Set();
  const countriesSet = new Set();
  geoFreshlings.forEach((f) => {
    const raw = (f.homeDz || '').trim();
    if (!raw) return;
    const m = dzMappings[raw.toLowerCase()];
    if (!m) return;
    const country = (m.country || 'US').trim().toUpperCase() || 'US';
    countriesSet.add(country);
    if (country === 'US' && m.state) {
      statesSet.add(m.state.toUpperCase());
    }
  });
  const usStateCount = statesSet.size;
  const countryCount = countriesSet.size;

  const activities = {
    highAltitude: highAltitudeJumps,
    jet: jetJumps,
    canopy: canopyUsed,
    water: waterUsed,
  };
  const hasAnyActivity =
    activities.highAltitude > 0 ||
    activities.jet > 0 ||
    activities.canopy > 0 ||
    activities.water > 0;

  const teaserSlides = [];
  const pushNum = (id, value, label) => {
    teaserSlides.push({ id, kind: 'number', value, label });
  };
  pushNum('freshlings', pairedFreshlingCount, 'Freshlings');
  pushNum('teams', totalTeams, 'Teams');
  pushNum('mentors', pairedMentors, 'Mentors');
  if (avgJumps != null) pushNum('avgJumps', avgJumps, 'Avg. jumps at start');
  if (ageRangeLabel) {
    teaserSlides.push({
      id: 'ages',
      kind: 'text',
      display: ageRangeLabel,
      label: 'Freshling ages',
    });
  }
  if (activities.highAltitude > 0) {
    pushNum('ha', activities.highAltitude, 'High altitude jumps');
  }
  if (activities.jet > 0) pushNum('jet', activities.jet, 'Jet jumps');
  if (activities.canopy > 0) pushNum('canopy', activities.canopy, 'Canopy courses');
  if (activities.water > 0) pushNum('water', activities.water, 'Water training');
  if (usStateCount > 0) pushNum('states', usStateCount, 'States');
  if (countryCount > 0) pushNum('countries', countryCount, 'Countries');

  return {
    teaserSlides,
    hero: { value: pairedFreshlingCount, label: 'Freshlings' },
    chips: [
      { id: 'teams', label: 'Teams', value: totalTeams, tone: 'slate' },
      { id: 'mentors', label: 'Mentors', value: pairedMentors, tone: 'amber' },
      ...(avgJumps != null
        ? [{ id: 'avgJumps', label: 'Avg. jumps at start', value: avgJumps, tone: 'violet' }]
        : []),
      ...(usStateCount > 0
        ? [{ id: 'states', label: 'States', value: usStateCount, tone: 'teal' }]
        : []),
      ...(countryCount > 0
        ? [{ id: 'countries', label: 'Countries', value: countryCount, tone: 'indigo' }]
        : []),
    ],
    ageRange: ageRangeLabel ? { label: 'Freshling ages', text: ageRangeLabel } : null,
    activities,
    hasAnyActivity,
  };
}

function chipValue(merged, id) {
  const c = (merged.chips || []).find((x) => x.id === id);
  return c != null && Number.isFinite(Number(c.value)) ? Number(c.value) : null;
}

function fmtCount(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return String(n);
}

/**
 * @param {object} opts
 * @param {object[]} opts.flyers - all flyers for the event
 * @param {object[]} opts.teams
 * @param {object} opts.dzMappings - raw name → { state, country }
 * @param {object|null} opts.statsOverrides - eventMemoryMeta.statsOverrides
 * @returns {Record<string, string>} merge tokens + sponsorEventSnapshotHtml
 */
export function buildSponsorFollowUpGlobalMergeFields({
  flyers,
  teams,
  dzMappings,
  statsOverrides,
}) {
  const list = Array.isArray(flyers) ? flyers : [];
  const teamList = Array.isArray(teams) ? teams.filter((t) => !t.test) : [];
  const dz = dzMappings && typeof dzMappings === 'object' ? dzMappings : {};

  const base = buildNumbersDataBase({
    flyers: list,
    nonTestTeams: teamList,
    dzMappings: dz,
  });
  const merged = applyMemoryBookStatsOverrides(base, statsOverrides) || base;

  const freshlings = fmtCount(merged.hero?.value);
  const teamsStr = fmtCount(chipValue(merged, 'teams'));
  const mentorsStr = fmtCount(chipValue(merged, 'mentors'));
  const avgJ = chipValue(merged, 'avgJumps');
  const avgJumpsStr = avgJ != null ? String(avgJ) : '—';
  const statesStr = fmtCount(chipValue(merged, 'states'));
  const countriesStr = fmtCount(chipValue(merged, 'countries'));
  const ageRangeStr = (merged.ageRange && merged.ageRange.text) || '—';
  const act = merged.activities || {};
  const highAltitudeStr = fmtCount(act.highAltitude);
  const jetJumpsStr = fmtCount(act.jet);
  const canopyStr = fmtCount(act.canopy);
  const waterStr = fmtCount(act.water);

  const li = 'style="margin:0 0 6px 0;font-size:14px;line-height:1.5;"';
  const snapLines = [];
  snapLines.push(
    `<li ${li}><strong>${escapeHtml(freshlings)}</strong> Freshlings, <strong>${escapeHtml(teamsStr)}</strong> teams, and <strong>${escapeHtml(mentorsStr)}</strong> mentors</li>`
  );
  snapLines.push(
    `<li ${li}><strong>${escapeHtml(avgJumpsStr)}</strong> average jumps at the start</li>`
  );
  snapLines.push(
    `<li ${li}>Participants from <strong>${escapeHtml(statesStr)}</strong> states and <strong>${escapeHtml(countriesStr)}</strong> countries</li>`
  );
  snapLines.push(`<li ${li}>Freshling ages ranged from <strong>${escapeHtml(ageRangeStr)}</strong></li>`);
  snapLines.push(
    `<li ${li}><strong>${escapeHtml(highAltitudeStr)}</strong> high altitude jumps and <strong>${escapeHtml(jetJumpsStr)}</strong> jet jumps</li>`
  );
  snapLines.push(
    `<li ${li}><strong>${escapeHtml(canopyStr)}</strong> canopy courses and <strong>${escapeHtml(waterStr)}</strong> water training participants</li>`
  );
  snapLines.push(
    `<li ${li}>Jumps and training provided in partnership with Skydive Perris</li>`
  );

  const sponsorEventSnapshotHtml = `<ul style="margin:12px 0;padding-left:1.2rem;">${snapLines.join('')}</ul>`;

  return {
    freshlings,
    teams: teamsStr,
    mentors: mentorsStr,
    avgJumps: avgJumpsStr,
    states: statesStr,
    countries: countriesStr,
    ageRange: ageRangeStr,
    highAltitude: highAltitudeStr,
    jetJumps: jetJumpsStr,
    canopyCourses: canopyStr,
    waterTraining: waterStr,
    sponsorEventSnapshotHtml,
  };
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
